import React, { useEffect, useRef, useState } from 'react';

/**
 * Counts up from 0 (or the previous value) to `value` over `duration` ms.
 * Purely presentational -- takes whatever numeric value the caller already
 * fetched from a real API response, never generates its own data.
 */
const AnimatedNumber = ({ value = 0, duration = 900, format, className }) => {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef(null);
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );

  useEffect(() => {
    const target = Number.isFinite(value) ? value : 0;

    if (prefersReducedMotion.current) {
      setDisplay(target);
      return;
    }

    const from = fromRef.current;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / duration);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (target - from) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  const formatted = format ? format(display) : Math.round(display).toLocaleString();

  return <span className={className}>{formatted}</span>;
};

export default AnimatedNumber;
